import { useEffect, useState } from "react";
import { Trophy, Coins, Loader2 } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { useAdminTheme } from "./AdminLayout";
import { useAdminFilters, FilterBar, cityParam } from "./filters";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const MEDAL = ["#F5C518", "#B8B8B8", "#CD7F32"];

export default function AdminReferrals() {
  const { accent, isCity } = useAdminTheme();
  const f = useAdminFilters();
  const [rows, setRows] = useState([]);
  const [rank, setRank] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/referrals"),
      api.get("/admin/referral/ranking", { params: { city_id: cityParam(f) } }),
    ]);
    setRows(a.data);
    setRank(b.data);
  };
  useEffect(() => { load().catch(() => {}); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.cityId]);

  const payout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/referral/ranking/payout", null, { params: { city_id: cityParam(f) } });
      toast.success(`Prêmios pagos: ${brl(data.total)} para ${data.paid.length} indicadores`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const total = rows.reduce((a, r) => a + r.earned_total, 0);
  const prizes = rank ? [rank.prizes.prize_top1, rank.prizes.prize_top2, rank.prizes.prize_top3] : [];

  return (
    <div data-testid="admin-referrals">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Rede de indicações</h1>
          <p className="text-sm text-gray-500">
            3,5% nível 1 + 2,5% nível 2 creditados na hora do pedido — total distribuído: <b>{brl(total)}</b>
          </p>
        </div>
        <FilterBar f={f} accent={accent} />
      </div>

      {rank && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 mt-6" data-testid="admin-ranking">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <Trophy size={18} className="text-mage-yellow" /> Ranking mensal de indicadores
              </h2>
              <p className="text-xs text-gray-500">
                {new Date(`${rank.month}-01T12:00:00`).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}
                {" · "}prêmios {prizes.map((p) => brl(p)).join(" · ")}
                {rank.city ? ` · ${rank.city.name}` : " · todas as cidades (selecione uma cidade para pagar os prêmios)"}
              </p>
            </div>
            {!isCity && (
              <Button data-testid="payout-prizes-button" onClick={payout}
                disabled={busy || rank.paid || rank.ranking.length === 0 || !cityParam(f)}
                title={!cityParam(f) ? "Selecione uma cidade para pagar os prêmios" : undefined}
                className="h-10 rounded-xl gf-gradient text-white font-bold gf-press">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <><Coins size={16} className="mr-1" /> {rank.paid ? "Prêmios pagos" : "Pagar prêmios do mês"}</>}
              </Button>
            )}
          </div>

          <div className="overflow-x-auto mt-3">
            <Table data-testid="ranking-table">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Indicador</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-right">Indicados</TableHead>
                  <TableHead className="text-right">Pedidos no mês</TableHead>
                  <TableHead className="text-right">Comissões no mês</TableHead>
                  <TableHead className="text-right">Prêmio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rank.ranking.map((r) => (
                  <TableRow key={r.user_id} data-testid={`admin-ranking-row-${r.position}`}>
                    <TableCell>
                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                        style={{ background: r.position <= 3 ? MEDAL[r.position - 1] : "#141414" }}>
                        {r.position}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold">{r.name}<br />
                      <span className="text-[11px] text-gray-500">{r.email} · {r.role}</span></TableCell>
                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                    <TableCell className="text-right">{r.indicated}</TableCell>
                    <TableCell className="text-right">{r.orders}</TableCell>
                    <TableCell className="text-right font-display font-extrabold text-gf-orange">{brl(r.earned)}</TableCell>
                    <TableCell className="text-right font-semibold text-mage-green">
                      {r.position <= 3 ? brl(prizes[r.position - 1]) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {rank.ranking.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-gray-500 py-6">
                    Nenhuma comissão registrada neste mês.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <h2 className="font-display font-bold text-lg mt-8">Acumulado histórico por indicador</h2>
      <div className="rounded-2xl border border-gray-200 bg-white mt-2 overflow-x-auto">
        <Table data-testid="referrals-table">
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead>Código</TableHead>
              <TableHead className="text-right">Indicados</TableHead>
              <TableHead className="text-right">Nível 1</TableHead>
              <TableHead className="text-right">Nível 2</TableHead>
              <TableHead className="text-right">Total ganho</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`referral-row-${r.email}`}>
                <TableCell className="font-semibold">{r.name}<br />
                  <span className="text-[11px] text-gray-500">{r.email}</span></TableCell>
                <TableCell className="text-xs text-gray-600">{r.role}</TableCell>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell className="text-right">{r.indicated}</TableCell>
                <TableCell className="text-right text-gf-orange font-semibold">{brl(r.earned_l1)}</TableCell>
                <TableCell className="text-right text-mage-navy font-semibold">{brl(r.earned_l2)}</TableCell>
                <TableCell className="text-right font-display font-extrabold">{brl(r.earned_total)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-gray-500 py-6">
                Nenhuma comissão de indicação registrada ainda.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
